import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { OrderTable } from '../components/OrderTable';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, RadioGroup, FormControlLabel, Radio, Snackbar, Alert } from '@mui/material';
import { getAcceptLanguageHeader, useI18n } from '../i18n';

const STATUSES = ['new', 'processing', 'completed', 'clarification', 'refund', 'pending', 'cancelled', 'failed'];
const SERVICES = ['TMCELL', 'TMCELL Stripe', 'TMPACK', 'TTelecom', 'ASTUint'];
const EDITABLE_PENDING_SERVICES = ['TMCELL', 'TTelecom', 'TMPACK', 'BELET', 'ASTUint'];
const OPERATORS = ['', 'Оператор 1', 'Оператор 2', 'Оператор 3'];

function randomInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function randomChoice(arr) { return arr[randomInt(0, arr.length - 1)]; }

const now = Date.now();
const monthMs = 30 * 24 * 60 * 60 * 1000;
const MOCK_ORDERS = Array.from({ length: 25 }).map((_, i) => {
  const status = randomChoice(STATUSES);
  const operator = status === 'new' ? '' : randomChoice(OPERATORS.slice(1));
  const createdAt = new Date(now - randomInt(0, monthMs)).toISOString();
  const requestedAmount = randomInt(100, 2000);
  return {
    id: i + 1,
    client_phone: '+79' + randomInt(600000000, 699999999),
    service_name: randomChoice(SERVICES),
    target_identifier: String(randomInt(60000000, 69999999)),
    requested_amount: `${requestedAmount} TMT`,
    payment_currency: 'RUB',
    paid_amount: randomInt(1000, 10000),
    payment_time: createdAt,
    created_at: createdAt,
    order_status: status,
    external_order_id: 'TM-' + randomInt(1000000, 9999999) + '-' + String(i + 1).padStart(3, '0'),
    referral_id: '79324222699',
    assigned_operator: operator,
    internal_note: i % 3 === 0 ? 'Тестовое примечание ' + (i + 1) : '',
    clarification_note: '',
    payment_gateway: randomChoice(['stripe', 'yookassa', 'paypal']),
    email: `client${i + 1}@example.com`,
  };
});

export function OrdersPage({ operator, searchTerm = '' }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [noteValues, setNoteValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, orderId: null, status: null });
  const [selectedPaymentSystem, setSelectedPaymentSystem] = useState('Bahbit');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [refundingOrderIds, setRefundingOrderIds] = useState({});
  const refundRequestsRef = useRef(new Set());

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity, autoHideDuration:3 });
  };

  useEffect(() => {
    if (!user?.api_token) return;
    setLoading(true);
    setError(null);
    fetch('/api/orders', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${user.api_token}`,
        'Accept-Language': getAcceptLanguageHeader(),
      },
    })
      .then(res => {
        if (!res.ok) throw new Error(t('Ошибка загрузки заказов'));
        return res.json();
      })
      .then(data => {
        setOrders(Array.isArray(data.orders) ? data.orders : []);
        setLoading(false);
      })
      .catch(e => {
        setError(t('Ошибка загрузки заказов. Показаны тестовые данные.'));
        logout();
        //setOrders(MOCK_ORDERS);
        setLoading(false);
      });

    // WebSocket для новых заказов
    const ws = new WebSocket('wss://admin.terbium.pro/ws/');
    //const ws = new WebSocket('ws://localhost:8081/ws/');
    let pingInterval = null;

    try {
      ws.onopen = () => {
        
        //Отдаем индификатор устройства
      
          // if(user.device_id) {
          //   console.log('send login', user.login, user.device_id);
          //   ws.send(JSON.stringify({ type: 'login', login: user.login, deviceId: user.device_id }));
          // }
        
          ws.send(JSON.stringify({
            type: 'login',
            login: user.login,
            deviceId:user.device_id
          }));
        
          
        // Запускаем ping каждые 2 минуты
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 120000);
      };
      
      ws.onerror = (event) => console.log('error', event);
      ws.onmessage = (event) => {
        try {
          
          const data = JSON.parse(event.data);

          if (data.action === 'logout') {
          
            localStorage.setItem('was_logged_out', '1');
            logout();
            return;
          }

          const newOrder = data.order || data;
          if (!newOrder || !newOrder.external_order_id) return;
         
          setOrders(prev => {
            // Проверяем, есть ли уже такой заказ по external_order_id
            const existingOrder = prev.find(o => o.external_order_id === newOrder.external_order_id);
            if (existingOrder) {
              // Всегда обновляем заказ
              return prev.map(o =>
                o.external_order_id === newOrder.external_order_id
                  ? { ...o, ...newOrder }
                  : o
              );
            }
            if (prev.some(o => o.external_order_id === newOrder.external_order_id)) return prev;
            // Добавляем новый заказ в начало списка
            return [newOrder, ...prev];
          });
        } catch (e) {
          // ignore parse errors
        }
      };
    } catch (e) {
      // ignore ws errors
    }
    return () => {
      if (ws) ws.close();
      if (pingInterval) clearInterval(pingInterval);
    };
  }, [user, logout, t]);

  const filteredOrders = useMemo(() => {
    let filtered = orders;
    if (searchTerm === '__MY__') {
     // filtered = filtered.filter(order => order.assigned_operator === user?.login);
      filtered = filtered.filter(order => order.assigned_operator === user?.name);
    } else if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        (order.external_order_id || '').toLowerCase().includes(term) ||
        (order.client_phone || '').toLowerCase().includes(term) ||
        (order.service_name || '').toLowerCase().includes(term) ||
        (order.target_identifier || '').toLowerCase().includes(term) ||
        (order.order_status || '').toLowerCase().includes(term) ||
        (order.assigned_operator || '').toLowerCase().includes(term)
      );
    }
    // Безопасно получаем строку времени для сортировки
    const getTime = order => {
      const t = order.payment_time || order.created_at || '';
      return typeof t === 'string' ? t.replace(' ', 'T') : '';
    };
    // Новая сортировка:
    // 1. assigned_operator === user.name и order_status !== 'completed' — в самом верху
    // 2. Остальные — по времени оплаты (от новых к старым)
    return filtered.slice().sort((a, b) => {
      const aIsMine = a.assigned_operator === user?.name && a.order_status !== 'completed';
      const bIsMine = b.assigned_operator === user?.name && b.order_status !== 'completed';
      if (aIsMine && !bIsMine) return -1;
      if (!aIsMine && bIsMine) return 1;
      // внутри группы сортируем по payment_time (от новых к старым)
      return new Date(getTime(b)) - new Date(getTime(a));
    });
  }, [orders, searchTerm, user?.name]);

  // Добавим функцию для PATCH-запроса
  const patchOrder = useCallback(async (id, data) => {
    if (!user?.api_token) throw new Error('Нет токена');
  
    const res = await fetch(`/api/orders/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.api_token}`,
        'Accept-Language': getAcceptLanguageHeader(),
      },
      body: JSON.stringify(data),
    });

    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload?.success === false) {
      throw new Error(payload?.error || 'Ошибка обновления заказа');
    }
    return payload;
  }, [user]);

  const requeueOrderJob = useCallback(async (id) => {
    if (!user?.api_token) throw new Error('Нет токена');

    const res = await fetch(`/api/v1/order-jobs/${id}/requeue`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${user.api_token}`,
        'Accept-Language': getAcceptLanguageHeader(),
      },
    });

    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload?.success === false) {
      throw new Error(payload?.error || 'Ошибка повторной постановки job');
    }
    return payload;
  }, [user]);

  const getAssignedOperatorForStatusChange = useCallback((order, status) => {
    if (status === 'new') {
      return '';
    }

    if (user?.role === 'admin') {
      return order?.assigned_operator || '';
    }

    return operator;
  }, [operator, user?.role]);

  const handleTake = async (orderId) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');
      const { local_edit, ...orderWithoutLocalEdit } = order;
      const updatedOrder = { ...orderWithoutLocalEdit, order_status: 'processing', assigned_operator: user.name };
      await patchOrder(orderId, updatedOrder);
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? updatedOrder
          : order
      ));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка обновления заказа'));
    }
  };

  const handleStatusChange = async (orderId, status) => {
    if (!status) return;
    if (status === 'clarification') {
      setNoteValues(prev => ({
        ...prev,
        [orderId]: { ...prev[orderId], showClarification: true, clarificationStatus: status }
      }));
      return;
    }
    if (status === 'completed') {
      setPaymentDialog({ open: true, orderId, status });
      setSelectedPaymentSystem('Bahbit');
      return;
    }
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');
      const { local_edit, ...orderWithoutLocalEdit } = order;
      const updatedOrder = {
        ...orderWithoutLocalEdit,
        order_status: status,
        assigned_operator: getAssignedOperatorForStatusChange(order, status),
      };
      await patchOrder(orderId, updatedOrder);
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? updatedOrder
          : order
      ));
      setNoteValues(prev => ({ ...prev, [orderId]: { ...prev[orderId], showClarification: false, clarification_note: '', clarificationStatus: undefined } }));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка обновления заказа'));
    }
  };

  const handleRefund = useCallback(async (orderId, refundNote) => {
    const numericOrderId = Number(orderId);
    if (refundRequestsRef.current.has(numericOrderId)) return;

    refundRequestsRef.current.add(numericOrderId);
    setRefundingOrderIds(prev => ({ ...prev, [numericOrderId]: true }));

    try {
      const order = orders.find(item => item.id === numericOrderId);
      if (!order) throw new Error('Order not found');

      const { local_edit, ...orderWithoutLocalEdit } = order;
      const requestedOrder = {
        ...orderWithoutLocalEdit,
        order_status: 'refund',
        assigned_operator: getAssignedOperatorForStatusChange(order, 'refund'),
        clarification_note: refundNote.trim(),
      };
      const payload = await patchOrder(numericOrderId, requestedOrder);
      const savedOrder = payload?.order
        ? { ...order, ...payload.order }
        : requestedOrder;

      setOrders(prev => prev.map(item => (
        item.id === numericOrderId ? savedOrder : item
      )));
      showSnackbar(t('Возврат выполнен'), 'success');
      return savedOrder;
    } catch (e) {
      showSnackbar(e.message || t('Ошибка возврата платежа'));
      throw e;
    } finally {
      refundRequestsRef.current.delete(numericOrderId);
      setRefundingOrderIds(prev => {
        const next = { ...prev };
        delete next[numericOrderId];
        return next;
      });
    }
  }, [getAssignedOperatorForStatusChange, orders, patchOrder, t]);

  const handlePaymentSystemConfirm = async () => {
    const { orderId, status } = paymentDialog;
    setPaymentDialog({ open: false, orderId: null, status: null });
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');
      const { local_edit, ...orderWithoutLocalEdit } = order;
      const updatedOrder = {
        ...orderWithoutLocalEdit,
        order_status: status,
        assigned_operator: getAssignedOperatorForStatusChange(order, status),
        payment_method: selectedPaymentSystem,
      };
      await patchOrder(orderId, updatedOrder);
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? updatedOrder
          : order
      ));
      setNoteValues(prev => ({ ...prev, [orderId]: { ...prev[orderId], showClarification: false, clarification_note: '' } }));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка обновления заказа'));
    }
  };

  const handleNoteChange = (orderId, value, field) => {
    setNoteValues(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], [field]: value }
    }));
  };

  const handleInternalNoteChange = async (orderId, value) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');
      const updatedOrder = { ...order, internal_note: value, local_edit: true };
      await patchOrder(orderId, updatedOrder);
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? updatedOrder
          : order
      ));
    } catch (e) {
      showSnackbar(e.message || 'Ошибка обновления заказа');
    }
  };

  const handlePendingOrderEditSave = useCallback(async (orderId, values, options = {}) => {
    const { requeue = false } = options;
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');

      const payload = {
        email: (values.email || '').trim(),
        phone_dist: (values.phone_dist || '').trim(),
        amount_dist: Number(values.amount_dist),
        service_name: (values.service_name || '').trim(),
      };

      const patchResult = await patchOrder(orderId, payload);
      const savedOrder = patchResult?.order ? { ...order, ...patchResult.order } : { ...order, ...payload };
      setOrders(prev => prev.map(item => (
        item.id === orderId ? { ...item, ...savedOrder } : item
      )));

      if (!requeue) {
        showSnackbar(t('Заказ обновлен'), 'success');
        return savedOrder;
      }

      const requeueResult = await requeueOrderJob(orderId);
      const requeuedOrder = requeueResult?.order ? { ...savedOrder, ...requeueResult.order } : { ...savedOrder, order_status: 'processing' };
      setOrders(prev => prev.map(item => (
        item.id === orderId ? { ...item, ...requeuedOrder } : item
      )));
      showSnackbar(t('Заказ сохранен и поставлен в повторную обработку'), 'success');
      return requeuedOrder;
    } catch (e) {
      showSnackbar(e.message || t('Ошибка обновления заказа'));
      throw e;
    }
  }, [orders, patchOrder, requeueOrderJob, t]);

  // Сохраняем сообщение клиенту и меняем статус на clarification, если оно заполнено
  React.useEffect(() => {
    Object.entries(noteValues).forEach(async ([orderId, val]) => {
      // CLARIFICATION
      if (val.showClarification && val.clarification_note && val.clarification_note.length > 0) {
        try {
          const order = orders.find(o => o.id === Number(orderId));
          if (!order) throw new Error('Order not found');
          const { local_edit, ...orderWithoutLocalEdit } = order;
          const nextStatus = val.clarificationStatus || 'clarification';
          const updatedOrder = {
            ...orderWithoutLocalEdit,
            order_status: nextStatus,
            assigned_operator: getAssignedOperatorForStatusChange(order, nextStatus),
            clarification_note: val.clarification_note,
          };
          await patchOrder(orderId, updatedOrder);
          setOrders(prev => prev.map(order =>
            order.id === Number(orderId)
              ? updatedOrder
              : order
          ));
          setNoteValues(prev => ({ ...prev, [orderId]: { ...prev[orderId], showClarification: false, clarification_note: '', clarificationStatus: undefined } }));
        } catch (e) {
          showSnackbar(e.message || 'Ошибка обновления заказа');
        }
      }
    });
  }, [getAssignedOperatorForStatusChange, noteValues, orders, patchOrder, t]);

  return (
    <div>
      <h1>{t('Заказы')}</h1>
      {loading && <div style={{margin: '24px 0'}}>{t('Загрузка...')}</div>}
      {error && <div style={{color: 'red', marginBottom: 12}}>{error}</div>}
      <OrderTable
        orders={filteredOrders}
        onTake={handleTake}
        onStatusChange={handleStatusChange}
        onRefund={handleRefund}
        refundingOrderIds={refundingOrderIds}
        onPendingOrderEditSave={handlePendingOrderEditSave}
        operator={operator}
        noteValues={noteValues}
        onNoteChange={handleNoteChange}
        onInternalNoteChange={handleInternalNoteChange}
        adminRole={user?.role}
        editableServices={EDITABLE_PENDING_SERVICES}
      />
      <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ open: false, orderId: null, status: null })}>
        <DialogTitle>{t('Выберите метод пополнения')}</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={selectedPaymentSystem}
            onChange={e => setSelectedPaymentSystem(e.target.value)}
          >
            <FormControlLabel value="Bahbit" control={<Radio />} label="Bahbit" />
            <FormControlLabel value="SIM" control={<Radio />} label="SIM" />
            <FormControlLabel value="Toleg" control={<Radio />} label="Toleg" />
            <FormControlLabel value="SIM/Bahbit" control={<Radio />} label="SIM/Bahbit" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog({ open: false, orderId: null, status: null })}>{t('Отмена')}</Button>
          <Button onClick={handlePaymentSystemConfirm} variant="contained" color="primary">{t('Подтвердить')}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
} 